import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    PageType,
    findFormPageByFormId,
    isEventPage,
    isEventWithEventContainerPage,
    isFormPage,
    type FormPage,
    type WorkspaceConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {configPageHref} from "~/workspaceConfig/configPageRoutes";
import {findConfigPageIdByType, findConfigJourneyProfilePageId} from "~/workspaceConfig/configPageIds";
import {hasWorkspaceUiConfig, loadOrCreateWorkspaceConfig} from "~/workspaceConfig/configRuntime";

function searchToQuery(search: Record<string, unknown> | undefined): string {
    if (search === undefined) {
        return "";
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
        if (value === undefined || value === null) {
            continue;
        }
        params.set(key, String(value));
    }
    const query = params.toString();
    return query.length === 0 ? "" : `?${query}`;
}

function loadConfig(workspaceId: Uuid): WorkspaceConfig {
    return loadOrCreateWorkspaceConfig(workspaceId);
}

function findFormPageByEventTemplateId(config: WorkspaceConfig, eventTemplateId: Uuid): FormPage | undefined {
    for (const page of Object.values(config.pages)) {
        if (isFormPage(page) !== true) {
            continue;
        }
        const form = config.forms[page.formId];
        if (form !== undefined && form.targetEntity !== "eventContainer" && form.eventTemplateId === eventTemplateId) {
            return page;
        }
    }
    return undefined;
}

export function resolveConfigFormHref(args: {
    workspaceId: Uuid;
    formId: Uuid;
    search?: Record<string, unknown>;
}): string | null {
    const page = findFormPageByFormId(loadConfig(args.workspaceId), args.formId);
    if (page === undefined) {
        return null;
    }
    return `${configPageHref(args.workspaceId, page.id)}${searchToQuery(args.search)}`;
}

export function resolveConfigFormHrefByEventTemplateId(args: {
    workspaceId: Uuid;
    eventTemplateId: Uuid;
    search?: Record<string, unknown>;
}): string | null {
    const page = findFormPageByEventTemplateId(loadConfig(args.workspaceId), args.eventTemplateId);
    if (page === undefined) {
        return null;
    }
    return `${configPageHref(args.workspaceId, page.id)}${searchToQuery(args.search)}`;
}

export function resolveConfigPageHrefByType(args: {
    workspaceId: Uuid;
    pageType: PageType | string;
    search?: Record<string, unknown>;
}): string | null {
    const pageId = findConfigPageIdByType(args.workspaceId, args.pageType);
    if (pageId === undefined) {
        return null;
    }
    return `${configPageHref(args.workspaceId, pageId)}${searchToQuery(args.search)}`;
}

export function resolveConfigEventListHref(args: {
    workspaceId: Uuid;
    eventTemplateId: Uuid;
    search?: Record<string, unknown>;
}): string | null {
    const config = loadConfig(args.workspaceId);
    for (const page of Object.values(config.pages)) {
        if (
            (isEventPage(page) === true || isEventWithEventContainerPage(page) === true)
            && page.eventTemplateId === args.eventTemplateId
        ) {
            return `${configPageHref(args.workspaceId, page.id)}${searchToQuery(args.search)}`;
        }
    }
    return null;
}

export function buildConfigJourneyProfileSearch(args: {
    eventContainerId: string;
    eventContainerTemplateId?: string | null;
}): {
    journeyId: string;
    eventContainerId: string;
    eventContainerTemplateId?: string;
} {
    return {
        journeyId: args.eventContainerId,
        eventContainerId: args.eventContainerId,
        ...(args.eventContainerTemplateId !== undefined && args.eventContainerTemplateId !== null
            ? {eventContainerTemplateId: args.eventContainerTemplateId}
            : {}),
    };
}

export function resolveConfigJourneyProfileHref(args: {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    eventContainerTemplateId: Uuid;
    search?: Record<string, unknown>;
}): string | null {
    const pageId = findConfigJourneyProfilePageId(args.workspaceId, args.eventContainerTemplateId);
    if (pageId === undefined) {
        return null;
    }
    return `${configPageHref(args.workspaceId, pageId)}${searchToQuery({
        ...args.search,
        ...buildConfigJourneyProfileSearch({
            eventContainerId: args.eventContainerId,
            eventContainerTemplateId: args.eventContainerTemplateId,
        }),
    })}`;
}

function orWorkspaceRoot(workspaceId: Uuid, href: string | null): string {
    return href ?? `/w/${workspaceId}`;
}

export function configFormHref(workspaceId: Uuid, formId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigFormHref({workspaceId, formId, search}));
}

export function configEventListHref(workspaceId: Uuid, eventTemplateId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigEventListHref({workspaceId, eventTemplateId, search}));
}

export function configJourneyProfileHref(
    workspaceId: Uuid,
    eventContainerId: Uuid,
    search: Record<string, unknown> | undefined,
    eventContainerTemplateId: Uuid,
): string {
    return orWorkspaceRoot(
        workspaceId,
        resolveConfigJourneyProfileHref({
            workspaceId: workspaceId,
            eventContainerId: eventContainerId,
            eventContainerTemplateId: eventContainerTemplateId,
            search: search,
        }),
    );
}

export function configEventContainerListHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(
        workspaceId,
        resolveConfigPageHrefByType({workspaceId, pageType: PageType.EventContainerWithEvents, search})
            ?? resolveConfigPageHrefByType({workspaceId, pageType: PageType.AgentView, search}),
    );
}

export function configDispositionTrackerHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.DispositionTracker, search}));
}

export function configAgentViewHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.AgentView, search}));
}

export function configUsersHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.Users, search}));
}

export function configUserInvitationHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.UserInvitation, search}));
}

export function resolveRoleHeaderPageHref(args: {
    headerItemType: "tickets";
    pageType: PageType;
    userTemplateId: Uuid;
    workspaceId: Uuid;
}): string | null {
    if (hasWorkspaceUiConfig(args.workspaceId) !== true) {
        return null;
    }
    const config = loadOrCreateWorkspaceConfig(args.workspaceId);
    const roleChrome = config.scaffold[args.userTemplateId];
    if (roleChrome === undefined) {
        return null;
    }
    const hasHeaderItem = roleChrome.header.some((item) => item.type === args.headerItemType);
    if (hasHeaderItem !== true) {
        return null;
    }
    return resolveConfigPageHrefByType({
        pageType: args.pageType,
        workspaceId: args.workspaceId,
    });
}

export function configTicketsHref(workspaceId: Uuid, ticketId?: Uuid): string {
    const search = ticketId === undefined ? undefined : {ticketId: ticketId};
    const configuredHref = resolveConfigPageHrefByType({
        pageType: PageType.Tickets,
        search: search,
        workspaceId: workspaceId,
    });
    if (configuredHref !== null) {
        return configuredHref;
    }
    return `/w/${workspaceId}/tickets${searchToQuery(search)}`;
}

export function configNotificationsHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return resolveConfigPageHrefByType({workspaceId, pageType: PageType.Notifications, search})
        ?? `/w/${workspaceId}/notifications`;
}

export function configDashboardHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.DashBoard, search}));
}

export function configLeaderboardHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(workspaceId, resolveConfigPageHrefByType({workspaceId, pageType: PageType.Leaderboard, search}));
}

export function configFrontendLeaderboardHref(workspaceId: Uuid, search?: Record<string, unknown>): string {
    return orWorkspaceRoot(
        workspaceId,
        resolveConfigPageHrefByType({
            pageType: PageType.FrontendLeaderboard,
            search: search,
            workspaceId: workspaceId,
        }),
    );
}
